import React from 'react';
import SearchBar from '../../Common/SearchBar/SearchBar';
import './NewsAggSearchBar.scss';
import DateRangePickerr from '../../Common/DateRangePickerr/DateRangePickerr';

/**
 * The `NewsAggSearchBar` component represents a search bar and form for news aggregation.
 *
 * This function is called in \src\Component\NewsAggregator\NewsAggregator.jsx
 *
 * @param {Function} onSearchSubmit - Callback function to handle form submission.
 * @param {string} selectedCompany - The selected company name.
 * @param {Array} options - The list of company name options for selection.
 * @param {Function} onCompanyChange - Callback function for handling company name selection change.
 * @param {Object} errors - Error messages for form validation.
 * @param {string} startDate - The selected start date.
 * @param {Function} onChangeStartDate - Callback function for handling start date change.
 * @param {string} endDate - The selected end date.
 * @param {Function} onChangeEndDate - Callback function for handling end date change.
 * @returns {JSX.Element} The rendered `NewsAggSearchBar` component.
 */
const NewsAggSearchBar = ({
  onSearchSumbiit,
  selectedCompany,
  options,
  onCompanyChange,
  errors,
  startDate,
  onChangeStartDate,
  endDate,
  onChangeEndDate,
}) => {
  return (
    <>
    <div className="nagg-hero">
      <div className="nagg-hero-inner">

        {/* Title */}
        <div className="nagg-title-row">
          <div className="nagg-title-icon">&#128240;</div>
          <div>
            <h2 className="nagg-title">News Aggregator</h2>
            <p className="nagg-subtitle">Search and analyse news for any listed company</p>
          </div>
        </div>
      </div>
    </div>

    {/* Sticky Form */}
    <div className="nagg-sticky-form">
      <div className="nagg-hero-inner">
        <form onSubmit={onSearchSumbiit} noValidate>
          <div className="nagg-form-row">

            {/* Company Name */}
            <div className="nagg-field">
              <label className="nagg-label">
                Company Name <span className="nagg-required">*</span>
              </label>
              <div className="nagg-input-wrap">
                <SearchBar
                  value={selectedCompany}
                  data={options}
                  onChange={onCompanyChange}
                  placeholder="Search company…"
                  allowClearSearch={true}
                  styled={{
                    background: "#fff",
                    padding: "1px 0",
                    borderRadius: "8px",
                    border: errors.companyName ? "1.5px solid #ef4444" : "1.5px solid #d1d5db",
                    minWidth: 220,
                    margin: 0,
                  }}
                  selectStyled={{ minWidth: "240px", maxWidth: "240px" }}
                />
              </div>
              {errors.companyName && (
                <p className="nagg-error">Company name is required</p>
              )}
            </div>

            {/* Divider */}
            <div className="nagg-divider" />

            {/* From Date */}
            <div className="nagg-field">
              <label className="nagg-label">
                From Date <span className="nagg-required">*</span>
              </label>
              <div className={`nagg-date-wrap${errors.startDates ? " nagg-date-wrap--error" : ""}`}>
                <DateRangePickerr value={startDate} onChange={onChangeStartDate} />
              </div>
              {errors.startDates && (
                <p className="nagg-error">{errors.startDatesErrMsg}</p>
              )}
            </div>

            {/* Divider */}
            <div className="nagg-divider" />

            {/* Till Date */}
            <div className="nagg-field">
              <label className="nagg-label">
                Till Date <span className="nagg-required">*</span>
              </label>
              <div className={`nagg-date-wrap${errors.endDates ? " nagg-date-wrap--error" : ""}`}>
                <DateRangePickerr value={endDate} onChange={onChangeEndDate} />
              </div>
              {errors.endDates && (
                <p className="nagg-error">{errors.endDatesErrMsg}</p>
              )}
            </div>

            {/* Search Button */}
            <div className="nagg-btn-wrap">
              <button type="submit" className="nagg-submit-btn">
                <i className="fas fa-search nagg-btn-icon" />
                Search
              </button>
            </div>

          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default NewsAggSearchBar;
